import { CheckIcon } from "@heroicons/react/24/outline";
import { BarsArrowDownIcon } from "@heroicons/react/24/solid";
import {
    COLLABORATOR_SENTINEL,
    CommunityId,
    EntityPipelineStageId,
    EVERYONE_SENTINEL,
    ReferenceBusinessId,
    referenceSearchQueryTerm,
    ReferralMappings,
    RegionId,
    UserId,
} from "@sp-crm/core";
import { useQuery } from "@tanstack/react-query";
import { graphqlFetcher } from "backend/fetcher";
import { StatelessSearchbarItem } from "components/community-search/search-bar/stateless-searchbar-item";
import { ReferenceSearchBar } from "components/references/dashboard-reference/reference-search-bar";
import {
    describeReferenceSort,
    ReferenceSearchSort,
    ReferenceSearchSortValues,
} from "components/references/dashboard-reference/reference-search-sort";
import { trpc, trpcClient } from "components/rpc/trpc";
import { Subnav } from "components/shared/subnav";
import { SelectableUser } from "components/shared/user-select";
import { SearchRequestPagination } from "components/simple-search/search-request-pagination";
import { fancyAlert } from "components/ui/fancy-alert";
import { fancyConfirm } from "components/ui/fancy-confirm";
import { SecondaryButton } from "components/ui/secondary-button";
import { Spinner } from "components/ui/spinner";
import {
    AdvancedSearchEntityType,
    ReferenceContactSearchDocument,
    ReferenceContactSearchQuery,
    ReferenceContactSearchQueryVariables,
    ReferenceContactSearchRequest,
    ReferenceOrganizationSearchDocument,
    ReferenceOrganizationSearchQuery,
    ReferenceOrganizationSearchQueryVariables,
    ReferenceOrganizationSearchRequest,
    useReferenceContactSearchQuery,
    useReferenceOrganizationSearchQuery,
} from "generated/graphql";
import React, { useCallback, useMemo, useState } from "react";
import {
    initialSearchFilter,
    ReferenceDashboardAspect,
    ReferenceFilter,
} from "store/slices/reference-dashboard";
import { parsePhone } from "util/phone";
import { pluralize } from "util/pluralize";
import { NO_NAME } from "util/text";
import { handleEvent } from "util/user-events";
import { PipelineEntityRef, PipelineStageSummary } from "./types";

const ADD_ALL_FETCH_LIMIT = 9999;
const ADD_CARDS_CHUNK_SIZE = 200;

type ContactHit = ReferenceContactSearchQuery["referenceContactSearch"]["hits"][0];
type OrganizationHit =
    ReferenceOrganizationSearchQuery["referenceOrganizationSearch"]["hits"][0];

interface SortValue {
    sort: string;
    sortDirection: "ASC" | "DESC";
}

interface PagingValue {
    page: number;
    perPage: number;
}

const initialSort: SortValue = { sort: "numberOfReferrals", sortDirection: "DESC" };
const initialPaging: PagingValue = { page: 0, perPage: 25 };

const actualAssignedUserId = (assignedUserId: SelectableUser): UserId | null =>
    assignedUserId &&
    assignedUserId !== EVERYONE_SENTINEL &&
    assignedUserId !== COLLABORATOR_SENTINEL
        ? (assignedUserId as UserId)
        : null;

const buildContactRequest = (
    filter: ReferenceFilter,
    sort: SortValue,
    paging: PagingValue,
): Omit<ReferenceContactSearchRequest, "regionId"> => ({
    page: paging.page,
    perPage: paging.perPage,
    search: referenceSearchQueryTerm(filter.search ?? ""),
    sort: sort.sort,
    sortDirection: sort.sortDirection,
    assignedUserId: actualAssignedUserId(filter.assignedUserId),
    organizationTypes: filter.orgTypes,
    communityTypes: filter.communityTypes,
    referralTierIds: filter.referralTierIds,
    bestDaysToVisit: filter.bestDaysToVisit,
    contactCondition: filter.contactCondition,
    businessCondition: filter.businessCondition,
    communityCondition: filter.communityCondition,
    geoParams: filter.geoParams,
});

const buildOrganizationRequest = (
    filter: ReferenceFilter,
    sort: SortValue,
    paging: PagingValue,
): Omit<ReferenceOrganizationSearchRequest, "regionId"> => ({
    page: paging.page,
    perPage: paging.perPage,
    search: referenceSearchQueryTerm(filter.search ?? ""),
    sort: sort.sort,
    sortDirection: sort.sortDirection,
    assignedUserId: actualAssignedUserId(filter.assignedUserId),
    organizationTypes: filter.orgTypes,
    communityTypes: filter.communityTypes,
    referralTierIds: filter.referralTierIds,
    bestDaysToVisit: filter.bestDaysToVisit,
    businessCondition: filter.businessCondition,
    communityCondition: filter.communityCondition,
    geoParams: filter.geoParams,
});

const organizationHitToEntity = (hit: OrganizationHit): PipelineEntityRef =>
    hit.entityType === AdvancedSearchEntityType.Community
        ? { entityType: "Community", entityId: hit.id as CommunityId }
        : { entityType: "ReferenceBusiness", entityId: hit.id as ReferenceBusinessId };

const entityKey = (entity: PipelineEntityRef): string =>
    `${entity.entityType}:${entity.entityId}`;

interface BoardReferralSourcePickerProps {
    stage: PipelineStageSummary;
    regionId: RegionId;
    onAdded: () => Promise<void>;
}

export const BoardReferralSourcePicker: React.FC<
    BoardReferralSourcePickerProps
> = props => {
    const { stage, regionId, onAdded } = props;
    const trpcContext = trpc.useTRPC();

    const [filter, setFilter] = useState<ReferenceFilter>(initialSearchFilter);
    const [activeTab, setActiveTab] = useState("contacts");
    const [contactSort, setContactSort] = useState<SortValue>(initialSort);
    const [organizationSort, setOrganizationSort] = useState<SortValue>(initialSort);
    const [contactPaging, setContactPaging] = useState<PagingValue>(initialPaging);
    const [organizationPaging, setOrganizationPaging] =
        useState<PagingValue>(initialPaging);
    const [pendingKey, setPendingKey] = useState<string | null>(null);
    const [bulkBusy, setBulkBusy] = useState(false);

    const contactRequest = useMemo(
        () => buildContactRequest(filter, contactSort, contactPaging),
        [filter, contactSort, contactPaging],
    );
    const organizationRequest = useMemo(
        () => buildOrganizationRequest(filter, organizationSort, organizationPaging),
        [filter, organizationSort, organizationPaging],
    );

    const contactsQuery = useReferenceContactSearchQuery(
        { search: { regionId, ...contactRequest } },
        { keepPreviousData: true },
    );
    const organizationsQuery = useReferenceOrganizationSearchQuery(
        { search: { regionId, ...organizationRequest } },
        { keepPreviousData: true },
    );

    const contactResults = useMemo(
        () =>
            contactsQuery.data?.referenceContactSearch ?? {
                hits: [] as ContactHit[],
                total: 0,
            },
        [contactsQuery.data],
    );
    const organizationResults = useMemo(() => {
        if (filter.contactCondition) {
            return { hits: [], total: 0 };
        }
        return (
            organizationsQuery.data?.referenceOrganizationSearch ?? {
                hits: [],
                total: 0,
            }
        );
    }, [filter.contactCondition, organizationsQuery.data]);

    const membershipsOptions = trpcContext.pipeline.memberships.queryOptions({
        pipelineId: stage.pipelineId,
        regionId,
    });
    const membershipsQuery = useQuery(membershipsOptions);
    const addedByKey = useMemo(
        () =>
            new Map(
                (membershipsQuery.data ?? []).map(membership => [
                    entityKey({
                        entityType: membership.entityType,
                        entityId: membership.entityId,
                    }),
                    membership.id,
                ]),
            ),
        [membershipsQuery.data],
    );

    const handleFilterChange = useCallback((next: ReferenceFilter) => {
        setFilter(next);
        setContactPaging(paging => ({ ...paging, page: 0 }));
        setOrganizationPaging(paging => ({ ...paging, page: 0 }));
    }, []);

    const handleClearGeoFilter = useCallback(() => {
        setFilter(current => ({ ...current, geoParams: null }));
    }, []);

    const sortValues: ReferenceSearchSortValues = useMemo(
        () => ({
            contact: contactSort,
            business: organizationSort,
            community: organizationSort,
            organization: organizationSort,
        }),
        [contactSort, organizationSort],
    );

    const handleSortChange = useCallback(
        (aspect: ReferenceDashboardAspect, sort: string, sortDirection: string) => {
            const value: SortValue = {
                sort,
                sortDirection: sortDirection === "ASC" ? "ASC" : "DESC",
            };
            if (aspect === "contact") {
                setContactSort(value);
                setContactPaging(paging => ({ ...paging, page: 0 }));
            } else {
                setOrganizationSort(value);
                setOrganizationPaging(paging => ({ ...paging, page: 0 }));
            }
        },
        [],
    );

    const refreshAfterChange = useCallback(async () => {
        await membershipsQuery.refetch();
        await onAdded();
    }, [membershipsQuery, onAdded]);

    const handleAddEntity = useCallback(
        async (entity: PipelineEntityRef) => {
            setPendingKey(entityKey(entity));
            handleEvent("referral-pipeline-add-card", { source: "pipeline" });
            try {
                await trpcClient.pipeline.addCard.mutate({
                    stageId: stage.id,
                    entityType: entity.entityType,
                    entityId: entity.entityId,
                });
            } catch (error) {
                await fancyAlert(
                    "Unable to add",
                    error instanceof Error
                        ? error.message
                        : "Unable to add this referral source",
                );
            } finally {
                setPendingKey(null);
                await refreshAfterChange();
            }
        },
        [stage.id, refreshAfterChange],
    );

    const handleRemoveEntity = useCallback(
        async (
            entity: PipelineEntityRef,
            displayName: string,
            membershipId: EntityPipelineStageId,
        ) => {
            const confirmed = await fancyConfirm(
                "Remove from pipeline",
                `Remove "${displayName || NO_NAME}" from this pipeline?`,
                "Yes, remove",
                "Cancel",
            );
            if (!confirmed) {
                return;
            }
            setPendingKey(entityKey(entity));
            handleEvent("referral-pipeline-remove-card", { source: "pipeline" });
            try {
                await trpcClient.pipeline.removeCard.mutate({ membershipId });
            } catch (error) {
                await fancyAlert(
                    "Unable to remove",
                    error instanceof Error
                        ? error.message
                        : "Unable to remove this referral source",
                );
            } finally {
                setPendingKey(null);
                await refreshAfterChange();
            }
        },
        [refreshAfterChange],
    );

    const fetchAllMatching = useCallback(async (): Promise<PipelineEntityRef[]> => {
        if (activeTab === "contacts") {
            const data = await graphqlFetcher<
                ReferenceContactSearchQuery,
                ReferenceContactSearchQueryVariables
            >(ReferenceContactSearchDocument, {
                search: {
                    regionId,
                    ...contactRequest,
                    page: 0,
                    perPage: ADD_ALL_FETCH_LIMIT,
                },
            })();
            return data.referenceContactSearch.hits.map(hit => ({
                entityType: "ReferenceContact",
                entityId: hit.id,
            }));
        }
        const data = await graphqlFetcher<
            ReferenceOrganizationSearchQuery,
            ReferenceOrganizationSearchQueryVariables
        >(ReferenceOrganizationSearchDocument, {
            search: {
                regionId,
                ...organizationRequest,
                page: 0,
                perPage: ADD_ALL_FETCH_LIMIT,
            },
        })();
        return data.referenceOrganizationSearch.hits.map(organizationHitToEntity);
    }, [activeTab, regionId, contactRequest, organizationRequest]);

    const handleAddAll = useCallback(async () => {
        const isContacts = activeTab === "contacts";
        const total = isContacts ? contactResults.total : organizationResults.total;
        const noun = isContacts
            ? pluralize(total, "contact", "contacts")
            : pluralize(total, "organization", "organizations");
        const confirmed = await fancyConfirm(
            "Add all matching referral sources",
            `Add the ${total} matching ${noun} to "${stage.name}"?`,
            "Yes, add all",
            "Cancel",
        );
        if (!confirmed) {
            return;
        }
        setBulkBusy(true);
        handleEvent("referral-pipeline-add-card", { source: "pipeline-add-all" });
        try {
            const entities = (await fetchAllMatching()).filter(
                entity => !addedByKey.has(entityKey(entity)),
            );
            for (let i = 0; i < entities.length; i += ADD_CARDS_CHUNK_SIZE) {
                await trpcClient.pipeline.addCards.mutate({
                    stageId: stage.id,
                    entities: entities.slice(i, i + ADD_CARDS_CHUNK_SIZE),
                });
            }
        } catch (error) {
            await fancyAlert(
                "Unable to add",
                error instanceof Error
                    ? error.message
                    : "Unable to add these referral sources",
            );
        } finally {
            setBulkBusy(false);
            await refreshAfterChange();
        }
    }, [
        activeTab,
        contactResults.total,
        organizationResults.total,
        stage.id,
        stage.name,
        fetchAllMatching,
        addedByKey,
        refreshAfterChange,
    ]);

    const handleRemoveAll = useCallback(async () => {
        const isContacts = activeTab === "contacts";
        setBulkBusy(true);
        try {
            const removable = (await fetchAllMatching()).filter(entity =>
                addedByKey.has(entityKey(entity)),
            );
            const noun = isContacts
                ? pluralize(removable.length, "contact", "contacts")
                : pluralize(removable.length, "organization", "organizations");
            if (removable.length === 0) {
                await fancyAlert(
                    "Nothing to remove",
                    `None of the matching ${noun} are in this pipeline.`,
                );
                return;
            }
            const confirmed = await fancyConfirm(
                "Remove matching referral sources",
                `Remove the ${removable.length} matching ${noun} from this pipeline?`,
                "Yes, remove all",
                "Cancel",
            );
            if (!confirmed) {
                return;
            }
            handleEvent("referral-pipeline-remove-card", {
                source: "pipeline-remove-all",
            });
            for (let i = 0; i < removable.length; i += ADD_CARDS_CHUNK_SIZE) {
                await trpcClient.pipeline.removeCards.mutate({
                    pipelineId: stage.pipelineId,
                    entities: removable.slice(i, i + ADD_CARDS_CHUNK_SIZE),
                });
            }
        } catch (error) {
            await fancyAlert(
                "Unable to remove",
                error instanceof Error
                    ? error.message
                    : "Unable to remove these referral sources",
            );
        } finally {
            setBulkBusy(false);
            await refreshAfterChange();
        }
    }, [activeTab, fetchAllMatching, addedByKey, stage.pipelineId, refreshAfterChange]);

    const renderResultsHeader = useCallback(
        (aspect: ReferenceDashboardAspect, total: number, noun: string) => (
            <div className="flex flex-wrap items-center justify-between gap-2 pb-2 pt-4">
                <div className="text-sm font-bold">
                    {total} {noun} found
                </div>
                <div className="flex items-center gap-2">
                    <StatelessSearchbarItem
                        content={
                            <div className="flex items-center space-x-2">
                                <BarsArrowDownIcon className="w-5 h-5 flex-shrink-0" />
                                <span className="text-sm">
                                    Sort:{" "}
                                    {describeReferenceSort(aspect, sortValues[aspect])}
                                </span>
                            </div>
                        }>
                        <ReferenceSearchSort
                            aspect={aspect}
                            values={sortValues}
                            onChange={handleSortChange}
                        />
                    </StatelessSearchbarItem>
                    <SecondaryButton
                        onClick={e => {
                            e.preventDefault();
                            handleAddAll();
                        }}
                        disabled={bulkBusy || total === 0}>
                        <span className="text-sm">
                            {bulkBusy ? "Working…" : `Add all ${total}`}
                        </span>
                    </SecondaryButton>
                    <SecondaryButton
                        onClick={e => {
                            e.preventDefault();
                            handleRemoveAll();
                        }}
                        disabled={bulkBusy || total === 0 || addedByKey.size === 0}>
                        <span className="text-sm">Remove all</span>
                    </SecondaryButton>
                </div>
            </div>
        ),
        [
            sortValues,
            handleSortChange,
            handleAddAll,
            handleRemoveAll,
            bulkBusy,
            addedByKey.size,
        ],
    );

    const renderCardAction = useCallback(
        (entity: PipelineEntityRef, displayName: string) => {
            const key = entityKey(entity);
            const membershipId = addedByKey.get(key);
            if (pendingKey === key) {
                return <Spinner />;
            }
            if (membershipId) {
                return (
                    <div className="flex flex-shrink-0 items-center gap-2">
                        <span className="inline-flex items-center gap-1 text-sm font-medium text-green-700">
                            <CheckIcon className="w-4 h-4" />
                            Added
                        </span>
                        <SecondaryButton
                            onClick={e => {
                                e.preventDefault();
                                handleRemoveEntity(entity, displayName, membershipId);
                            }}
                            disabled={pendingKey !== null || bulkBusy}>
                            <span className="text-sm">Remove</span>
                        </SecondaryButton>
                    </div>
                );
            }
            return (
                <SecondaryButton
                    onClick={e => {
                        e.preventDefault();
                        handleAddEntity(entity);
                    }}
                    disabled={pendingKey !== null || bulkBusy}>
                    <span className="text-sm">Add</span>
                </SecondaryButton>
            );
        },
        [addedByKey, pendingKey, bulkBusy, handleAddEntity, handleRemoveEntity],
    );

    const renderContacts = useCallback(
        () => (
            <div className="pb-4">
                {renderResultsHeader(
                    "contact",
                    contactResults.total,
                    pluralize(contactResults.total, "contact", "contacts"),
                )}
                {contactsQuery.isLoading ? <Spinner /> : null}
                <ul className="space-y-2">
                    {contactResults.hits.map(hit => (
                        <li key={hit.id}>
                            <PickerContactCard
                                hit={hit}
                                action={renderCardAction(
                                    {
                                        entityType: "ReferenceContact",
                                        entityId: hit.id,
                                    },
                                    hit.name ?? "",
                                )}
                            />
                        </li>
                    ))}
                </ul>
                {contactsQuery.data ? (
                    <SearchRequestPagination
                        request={{ ...contactPaging, ...contactSort }}
                        total={contactResults.total}
                        onPageChange={page =>
                            setContactPaging(paging => ({ ...paging, page }))
                        }
                        onPageSizeChange={perPage =>
                            setContactPaging({ page: 0, perPage })
                        }
                    />
                ) : null}
            </div>
        ),
        [
            renderResultsHeader,
            contactResults,
            contactsQuery.isLoading,
            contactsQuery.data,
            contactPaging,
            contactSort,
            renderCardAction,
        ],
    );

    const renderOrganizations = useCallback(
        () => (
            <div className="pb-4">
                {renderResultsHeader(
                    "organization",
                    organizationResults.total,
                    pluralize(organizationResults.total, "organization", "organizations"),
                )}
                {organizationsQuery.isLoading ? <Spinner /> : null}
                <ul className="space-y-2">
                    {organizationResults.hits.map(hit => (
                        <li key={hit.id}>
                            <PickerOrganizationCard
                                hit={hit}
                                action={renderCardAction(
                                    organizationHitToEntity(hit),
                                    hit.name ?? "",
                                )}
                            />
                        </li>
                    ))}
                </ul>
                {organizationsQuery.data ? (
                    <SearchRequestPagination
                        request={{ ...organizationPaging, ...organizationSort }}
                        total={organizationResults.total}
                        onPageChange={page =>
                            setOrganizationPaging(paging => ({ ...paging, page }))
                        }
                        onPageSizeChange={perPage =>
                            setOrganizationPaging({ page: 0, perPage })
                        }
                    />
                ) : null}
            </div>
        ),
        [
            renderResultsHeader,
            organizationResults,
            organizationsQuery.isLoading,
            organizationsQuery.data,
            organizationPaging,
            organizationSort,
            renderCardAction,
        ],
    );

    const subpages = [
        {
            linkText: "Contacts",
            subpage: "contacts",
            render: renderContacts,
            badge: { count: contactResults.total, type: "info" as const },
        },
        {
            linkText: "Organizations",
            subpage: "organizations",
            render: renderOrganizations,
            badge: { count: organizationResults.total, type: "info" as const },
        },
    ];

    return (
        <div className="space-y-1 lg:space-y-2">
            <ReferenceSearchBar
                value={filter}
                onChange={handleFilterChange}
                onResetMapView={handleClearGeoFilter}
                layout="stacked"
            />
            <Subnav
                subpages={subpages}
                defaultSubpage="contacts"
                selectedSubpage={activeTab}
                navigate={setActiveTab}
            />
        </div>
    );
};

interface PickerContactCardProps {
    hit: ContactHit;
    action: React.ReactNode;
}

const PickerContactCard: React.FC<PickerContactCardProps> = props => {
    const { hit, action } = props;
    const orgTypeMapping =
        hit.organizationType && ReferralMappings[hit.organizationType]
            ? ReferralMappings[hit.organizationType]
            : null;
    const phone = parsePhone(hit.phone ?? hit.cellPhone ?? "");
    const detailLine = [hit.organizationName, orgTypeMapping?.humanReadable]
        .filter(Boolean)
        .join(" · ");
    const contactLine = [hit.email, phone.digits !== "" ? phone.display : null]
        .filter(Boolean)
        .join(" · ");

    return (
        <div className="flex items-center justify-between gap-3 rounded-md border border-gray-200 bg-white px-4 py-3 shadow-sm">
            <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-baseline gap-x-2">
                    <span className="font-medium text-gray-900">
                        {hit.name || NO_NAME}
                    </span>
                    {hit.role ? (
                        <span className="text-xs text-gray-500">{hit.role}</span>
                    ) : null}
                </div>
                {detailLine ? (
                    <div className="truncate text-sm text-gray-600">{detailLine}</div>
                ) : null}
                {contactLine ? (
                    <div className="truncate text-sm text-gray-500">{contactLine}</div>
                ) : null}
            </div>
            {action}
        </div>
    );
};

interface PickerOrganizationCardProps {
    hit: OrganizationHit;
    action: React.ReactNode;
}

const PickerOrganizationCard: React.FC<PickerOrganizationCardProps> = props => {
    const { hit, action } = props;
    const orgTypeMapping =
        hit.organizationType && ReferralMappings[hit.organizationType]
            ? ReferralMappings[hit.organizationType]
            : null;
    const typeLabel =
        hit.entityType === AdvancedSearchEntityType.Community
            ? "Community"
            : orgTypeMapping?.humanReadable;
    const phone = parsePhone(hit.phone ?? "");
    const detailLine = [typeLabel, [hit.city, hit.state].filter(Boolean).join(", ")]
        .filter(Boolean)
        .join(" · ");
    const contactLine = [hit.email, phone.digits !== "" ? phone.display : null]
        .filter(Boolean)
        .join(" · ");

    return (
        <div className="flex items-center justify-between gap-3 rounded-md border border-gray-200 bg-white px-4 py-3 shadow-sm">
            <div className="min-w-0 flex-1">
                <div className="font-medium text-gray-900">{hit.name || NO_NAME}</div>
                {detailLine ? (
                    <div className="truncate text-sm text-gray-600">{detailLine}</div>
                ) : null}
                {contactLine ? (
                    <div className="truncate text-sm text-gray-500">{contactLine}</div>
                ) : null}
            </div>
            {action}
        </div>
    );
};
